import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Drawer, IconButton, Paper, Stack, Typography, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AnalyticsService from '../../../shared/services/analytics/analytics.service';
import { IHotspotDetail, IHotspotItem } from '../../../shared/services/analytics/hotspots.model';
import { getMixedColorPalette } from '../../../shared/utils/colors-utils';
import { LoadingSpinner } from '../display-elements-components/loading-spinner';
import { GraphBarChartGrouped } from './graphs/graph-bar-chart-grouped';
import { GraphBarChart } from './graphs/graph-bar-chart';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? '–' : `${v.toFixed(1)}%`;

/**
 * A chart mark the user clicked to drill into. `item` carries the richer ranked-hotspot stats
 * (share, delta, isNew) and is present only when the drill starts from the Hotspot Analysis page;
 * from an ordinary dashboard chart only `value` is known, and the rest is derived from the fetched
 * detail's monthly series.
 */
export interface IDrillSelection {
  dimension: string;
  id: string;
  label: string;
  value?: number;
  item?: IHotspotItem;
}

const Stat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant='h6' sx={{ color: color || 'text.primary' }}>{value}</Typography>
    <Typography variant='caption' color='text.secondary'>{label}</Typography>
  </Box>
);

export const DrillDownDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  selection: IDrillSelection | null;
  reportPeriodId: string;
  periodLabel?: string;
  scopeTwoMethod: 'location' | 'market';
  comparisonReportPeriodId?: string;
  dimensionLabel: string;
}> = ({ open, onClose, selection, reportPeriodId, periodLabel, scopeTwoMethod, comparisonReportPeriodId, dimensionLabel }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const analyticsService = useMemo(() => new AnalyticsService(), []);
  const [detail, setDetail] = useState<IHotspotDetail>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open || !selection || !reportPeriodId) return;
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage('');
    setDetail(undefined);
    analyticsService
      .getHotspotDetail(reportPeriodId, {
        dimension: selection.dimension,
        id: selection.id,
        label: selection.label,
        scopeTwoMethod,
        comparisonReportPeriodId,
      })
      .then((response) => { if (!cancelled) setDetail(response); })
      .catch((err) => { if (!cancelled) { console.log(err); setErrorMessage('Could not load detail. Please try again.'); } })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [open, selection, reportPeriodId, scopeTwoMethod, comparisonReportPeriodId, analyticsService]);

  const item = selection?.item;

  // The clicked mark carries a ranked-hotspot item on the Hotspot page; elsewhere only its value is
  // known, so fall back to the value passed with the selection and then to the detail's own current
  // total once it arrives. Same for the prior-period delta: use the item's when present, otherwise
  // derive it from this-vs-last month sums.
  const monthlyCurrentTotal = (detail?.monthly ?? []).reduce((sum, m) => sum + (m.current || 0), 0);
  const hasPrevious = !!detail?.comparison && (detail?.monthly ?? []).some((m) => m.previous !== null);
  const monthlyPrevTotal = (detail?.monthly ?? []).reduce((sum, m) => sum + (m.previous ?? 0), 0);
  const emissionsValue = item?.value ?? selection?.value ?? (detail ? monthlyCurrentTotal : undefined);
  const delta = item ? (item.deltaAbs ?? null) : (hasPrevious ? monthlyCurrentTotal - monthlyPrevTotal : null);
  const isNew = item?.isNew ?? false;
  const isDecrease = delta !== null && delta < 0;
  const isNoChange = delta !== null && Math.abs(delta) < 0.005;
  const deltaColor = isNew ? 'warning.main' : isNoChange ? 'text.secondary' : isDecrease ? 'success.main' : 'error.main';
  const DeltaIcon = isNoChange ? RemoveIcon : isDecrease ? ArrowDownwardIcon : ArrowUpwardIcon;
  // The delta row needs a comparison to mean anything: show it when the item already carries one, or
  // once the detail confirms a comparison period is in play.
  const showDelta = !!item || hasPrevious || isNew;

  const monthDataset = (detail?.monthly ?? []).map((m) => ({
    month: m.month,
    previous: m.previous ?? 0,
    current: m.current,
  }));
  const monthSeries = hasPrevious
    ? [
      { dataKey: 'previous', label: detail?.comparison?.friendlyName || 'Prior period' },
      { dataKey: 'current', label: periodLabel || 'This period' },
    ]
    : [{ dataKey: 'current', label: periodLabel || 'This period' }];
  const monthColors = [(theme.palette as any).lightGrey?.dark || '#b8b8b8', theme.palette.primary.main];

  const subItems = detail?.subBreakdown?.items ?? [];
  const subColors = getMixedColorPalette(theme.palette.primary.main);

  const viewRecords = () => {
    if (!detail) return;
    const { field, id } = detail.recordsFilter;
    if (field === 'scope') navigate(`/dashboard/scope/${id}`);
    else navigate(`/emissions/overview/table?${field}=${encodeURIComponent(id)}`);
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          top: { xs: 0, sm: 64 },
          height: { xs: '100%', sm: 'calc(100vh - 64px)' },
          borderTopLeftRadius: { xs: 0, sm: 24 },
          borderBottomLeftRadius: { xs: 0, sm: 24 },
          overflow: 'hidden',
          boxShadow: '-12px 0 32px rgba(17, 30, 33, 0.14)',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 580 }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fbfcfe' }}>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ px: 3, py: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='caption' color='text.secondary'>{dimensionLabel}</Typography>
            <Typography variant='h6' sx={{ color: 'text.primary', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {selection?.label}
            </Typography>
          </Box>
          <IconButton aria-label='Close' onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
          {/* Key stats */}
          {selection && (
            <Paper variant='outlined' sx={{ p: 2.5, mb: 2 }}>
              <Stack direction='row' flexWrap='wrap' useFlexGap sx={{ gap: 3 }}>
                <Stat label='Emissions' value={emissionsValue === undefined ? '–' : `${fmtTco2e.format(emissionsValue)} tCO₂e`} />
                {item?.sharePct !== undefined && item?.sharePct !== null && (
                  <Stat label={`% of ${dimensionLabel.toLowerCase()}`} value={fmtPct(item.sharePct)} />
                )}
                {showDelta && (
                  <Box>
                    <Stack direction='row' alignItems='center' spacing={0.25}>
                      {isNew
                        ? <Chip size='small' label='New' color='warning' variant='outlined' />
                        : delta === null
                          ? <Typography variant='h6' color='text.secondary'>–</Typography>
                          : (
                            <>
                              <DeltaIcon sx={{ fontSize: '1rem', color: deltaColor }} />
                              <Typography variant='h6' sx={{ color: deltaColor }}>
                                {fmtTco2e.format(Math.abs(delta))}
                              </Typography>
                            </>
                          )}
                    </Stack>
                    <Typography variant='caption' color='text.secondary'>
                      {detail?.comparison ? `vs ${detail.comparison.friendlyName}` : 'vs prior period'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <LoadingSpinner />
            </Box>
          )}
          {!isLoading && errorMessage && <Typography color='error' sx={{ py: 4 }}>{errorMessage}</Typography>}

          {!isLoading && !errorMessage && detail && (
            <Stack spacing={2}>
              <GraphBarChartGrouped
                title='Emissions by month'
                tooltip='Monthly emissions for this selection, this period vs the same calendar month in the comparison period.'
                dataset={monthDataset}
                series={monthSeries}
                colors={monthColors}
                chartHeight={300}
              />
              {subItems.length > 0 && (
                <GraphBarChart
                  title={`What's inside, by ${detail.subBreakdown.dimension}`}
                  tooltip={`This selection's emissions broken down by ${detail.subBreakdown.dimension}.`}
                  dataset={subItems}
                  series={[{ dataKey: 'value', label: 'tCO₂e' }]}
                  dataKey='label'
                  layout='horizontal'
                  colors={subColors}
                  chartHeight={Math.max(220, subItems.length * 44)}
                  horizontalLabelGutter={160}
                />
              )}
              <Button variant='outlined' endIcon={<ArrowForwardIcon />} onClick={viewRecords} sx={{ alignSelf: 'flex-start' }}>
                View emission records
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
